import { GQCompanySbtCommitmentStage } from '@watershed/shared-universal/generated/graphql-schema-types';

import { GQCompanyClimateCommitmentRawAllFieldsFragment } from '@watershed/shared-universal/generated/graphql';
import assertNever from '@watershed/shared-util/src/assertNever';
import max from 'lodash/max';

// Because we always wipe all old commitments, we use the createdAt field to determine the last
// time SBT's Excel showed modifications to the supplier's commitments/targets. This also
// prevents any migrations that modify the company_climate_commitments table from affecting
// the last updated date.
export function getSbtCommitmentLastUpdatedFromCommitments(
  commitments: Array<
    Pick<GQCompanyClimateCommitmentRawAllFieldsFragment, 'createdAt' | 'kind'>
  >
): Date | null {
  const sbtCommitments = commitments.filter(
    (commitment) => commitment.kind === 'ScienceBasedTarget'
  );

  return max(sbtCommitments.map((commitment) => commitment.createdAt)) ?? null;
}

export function getSbtCommitmentStageFromCommitments(
  commitments: Array<
    Pick<
      GQCompanyClimateCommitmentRawAllFieldsFragment,
      | 'sbtCommitmentStage'
      | 'sbtCommitmentStageNearTerm'
      | 'sbtCommitmentStageLongTerm'
      | 'sbtCommitmentStageNetZero'
      | 'kind'
    >
  >,
  term?: 'nearTerm' | 'longTerm' | 'netZero'
): GQCompanySbtCommitmentStage {
  const sbtCommitments = commitments.filter(
    (commitment) => commitment.kind === 'ScienceBasedTarget'
  );

  const commitmentStages = sbtCommitments.map((commitment) => {
    switch (term) {
      case 'nearTerm':
        return commitment.sbtCommitmentStageNearTerm;
      case 'longTerm':
        return commitment.sbtCommitmentStageLongTerm;
      case 'netZero':
        return commitment.sbtCommitmentStageNetZero;
      case undefined:
        return commitment.sbtCommitmentStage;
      default:
        assertNever(term);
    }
  });

  const committed = commitmentStages.find(
    (commitment) => commitment === 'Committed'
  );
  const targetsSet = commitmentStages.find(
    (commitment) => commitment === 'TargetsSet'
  );
  const removed = commitmentStages.find(
    (commitment) => commitment === 'Removed'
  );

  if (targetsSet) {
    return GQCompanySbtCommitmentStage.TargetsSet;
  } else if (committed) {
    return GQCompanySbtCommitmentStage.Committed;
  } else if (removed) {
    return GQCompanySbtCommitmentStage.Removed;
  }
  return GQCompanySbtCommitmentStage.None;
}
